import { type TeamMemberTableColumnKey } from '@/features/iam/teams/TeamMembersViewModel'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { IconButton } from '@/components/IconButton'
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  type TableSortOrder,
} from '@/components/Table'
import styles from './MembersTable.module.css'
import {
  Dropdown,
  DropdownItem,
  DropdownDivider,
  DropdownTitle,
  DropdownItemDescription,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { dateFormatters } from '@/utils/date-util'

interface TeamMemberTableColumn {
  header: string
  key: TeamMemberTableColumnKey
  sortable?: boolean
}

const teamMemberTableColumns: TeamMemberTableColumn[] = [
  {
    header: 'Name',
    key: 'member.displayName',
    sortable: true,
  },
  {
    header: 'Role',
    key: 'teamRole.name',
    sortable: false,
  },
  {
    header: 'Date Joined',
    key: 'dateCreated',
    sortable: true,
  },
]

export interface MembersTableRole {
  id: string
  name: string
  description: string
}

export interface MembersTableRow {
  id: string
  avatar: AvatarSubject
  displayName: string
  email: string
  roleName: string
  dateCreated: Date
  onChangeRole: (newRoleId: string) => void
  onRemove: () => void
}

export interface MembersTableProps {
  rows: MembersTableRow[]
  teamRoles: MembersTableRole[]
  canManageMember: boolean
  currentSort: TeamMemberTableColumnKey
  currentSortOrder: TableSortOrder
  onSort: (
    sortField: TeamMemberTableColumnKey,
    sortOrder: TableSortOrder,
  ) => void
}

export function MembersTable({
  rows,
  teamRoles,
  canManageMember,
  currentSort,
  currentSortOrder,
  onSort,
}: MembersTableProps) {
  return (
    <div>
      <Table striped stickyHeader>
        <TableHead>
          {teamMemberTableColumns.map((col) => (
            <TableHeader
              key={col.key}
              align="left"
              canSort={col.sortable}
              sortOrder={col.key === currentSort ? currentSortOrder : 'none'}
              onSort={(newSort) => onSort(col.key, newSort)}
            >
              {col.header}
            </TableHeader>
          ))}
          <TableHeader />
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            return (
              <TableRow key={row.id}>
                <TableCell>
                  <div className={styles.memberItem}>
                    <div>
                      <Avatar size={32} subject={row.avatar} />
                    </div>
                    <div className={styles.itemInfo}>
                      <p className={styles.infoTitle}>{row.displayName}</p>
                      <p>{row.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>{row.roleName}</TableCell>
                <TableCell width={200}>
                  {dateFormatters.dateNumericPaddedDay.format(row.dateCreated)}
                </TableCell>
                <TableCell align="right" width={25}>
                  <TeamMemberActions
                    teamRoles={teamRoles}
                    memberRoleName={row.roleName}
                    canManageMember={canManageMember}
                    onChangeRole={row.onChangeRole}
                    onRemove={row.onRemove}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

interface MemberActionsProps {
  teamRoles: MembersTableRole[]
  memberRoleName: string
  canManageMember: boolean
  onChangeRole: (newRoleId: string) => void
  onRemove: () => void
}

function TeamMemberActions({
  teamRoles,
  memberRoleName,
  canManageMember,
  onChangeRole,
  onRemove,
}: MemberActionsProps) {
  return (
    <Dropdown
      autoDismiss={true}
      position="bottom-end"
      button={
        <IconButton
          name="more"
          label="Member actions"
          className={styles.memberActions}
        />
      }
    >
      <DropdownTitle>Change Role</DropdownTitle>
      {teamRoles.map((role) => {
        return (
          <DropdownItem
            onPress={() => onChangeRole(role.id)}
            isDisabled={role.name === memberRoleName || !canManageMember}
            active={role.name === memberRoleName}
            checkmark={role.name === memberRoleName}
            key={role.id}
          >
            <DropdownItemTitle>{role.name}</DropdownItemTitle>
            <DropdownItemDescription>
              {role.description}
            </DropdownItemDescription>
          </DropdownItem>
        )
      })}
      {canManageMember && (
        <>
          <DropdownDivider />
          <DropdownItem onPress={() => onRemove()}>
            <DropdownItemTitle>Remove Member</DropdownItemTitle>
            <DropdownItemDescription>
              Remove this user from this team
            </DropdownItemDescription>
          </DropdownItem>
        </>
      )}
    </Dropdown>
  )
}
